import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `marketplace.gallery` in Brazilian Portuguese.
 *
 * Declared `: Messages['marketplace']['gallery']`, never inferred — that declaration IS the
 * completeness guarantee (PRD-27 LOC-011). Register is `você`, informal-professional
 * (LOC-061), and the ratified vocabulary of ADR-0018 is binding here: *agente*, *base de
 * conhecimento*, *conversa* (never *chat*, never *bate-papo*), *trecho*, *grupo*,
 * *habilidade*, and the bound phrases *Quem pode usar* / *Pertence a* / *todas as pessoas que
 * entram* / *Só quem mantém*.
 *
 * ── Two things a reader of the English will notice are different on purpose ──
 *
 * *Assistants shared with you* becomes **agentes**, not *assistentes*: ADR-0018 reserves
 * *assistente* for the product itself, so using it for the things a person picks between
 * would collapse two ideas the interface keeps apart. The one place *Assistente* does appear
 * is `chat.unknownAgent`, where the English says *Assistant* and means exactly the product.
 *
 * Every phrase the English builds on *its managers* / *the people who maintain it* is written
 * with **quem** rather than with *gestores* or *criadores*, because *gestor/gestora* and
 * *criador/criadora* are gendered and this copy addresses a group whose composition nothing
 * here knows (D-21). *Só quem mantém* is the bound phrase, and the rest follow its shape.
 */
export const gallery: Messages['marketplace']['gallery'] = {
  gallery: {
    title: 'Agentes',
    subtitle: 'Agentes compartilhados com você. Cada um diz de antemão o que sabe e o que pode fazer.',
    searchLabel: 'Buscar agentes',
    searchPlaceholder: 'Busque por nome ou pelo que ele faz…',
    categoryLabel: 'Categoria',
    categoryAll: 'Todas as categorias',
    newAgent: 'Novo agente',
    mine: 'Seus agentes',
    studio: 'Avançado',
    sections: {
      mine: 'Seus',
      featured: 'Em destaque',
      sharedWithYourGroups: 'Compartilhados com seus grupos',
      recent: 'Usados recentemente',
      all: 'Todos os agentes'
    },
    empty: {
      title: 'Nenhum agente foi compartilhado com você ainda',
      body: 'Quando alguém publicar um agente para você, ele aparece aqui. Você também pode criar o seu.'
    },
    emptySearch: {
      title: 'Nada corresponde a isso',
      body: 'Tente uma busca mais curta ou limpe o filtro de categoria para procurar em tudo que foi compartilhado com você.'
    },
    failed: {
      title: 'Não foi possível carregar a lista de agentes',
      body: 'Isso costuma ser temporário. Recarregue a página e avise um administrador se continuar acontecendo.'
    },
    clearFilters: 'Limpar filtros',
    loading: 'Carregando agentes…'
  },

  card: {
    officialLabel: 'Oficial',
    officialTitle: 'Publicado por esta empresa e confirmado por um administrador.',
    ownerLine: (name) => `de ${name}`,
    /**
     * *base* em vez de *base de conhecimento*: o selo tem uma linha de 10px ao lado de um
     * ícone, e o termo inteiro estoura a largura do cartão. O substantivo completo aparece
     * logo abaixo, no painel de divulgação, onde há espaço para ele.
     *
     * Zero cai na categoria `one` em português ("0 base"), e é exatamente por isso que a
     * escolha passa por `pluralCategory` e não por uma comparação com 1 (D-19).
     */
    knowledgeBadge: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `Conhecimento · ${fmt.formatCount(count)} base`
        : `Conhecimento · ${fmt.formatCount(count)} bases`,
    actionsBadge: 'Ações — sempre pergunta antes',
    memoryBadge: 'Lembra preferências',
    stateDraft: 'Rascunho — só você vê',
    stateArchived: 'Arquivado',
    stateSuspended: 'Pausado por um administrador',
    needsAttention: 'Precisa da sua atenção',
    needsAttentionHint: 'Abra para ver o que corrigir.'
  },

  about: {
    back: 'Todos os agentes',
    startChat: 'Iniciar conversa',
    starting: 'Iniciando…',
    startFailed: 'Não foi possível iniciar a conversa. Tente de novo em instantes.',
    startersTitle: 'Experimente perguntar',
    starterHint: 'Escolher uma preenche a mensagem para você editar antes de enviar.',
    saveCopy: 'Salvar uma cópia',
    copying: 'Copiando…',
    copyFailed: 'Não foi possível fazer a cópia. Tente de novo em instantes.',
    copyDoneTitle: 'Sua cópia está pronta',
    copyDoneBody: 'Ela começa como um rascunho que só você vê, então dá para mudar antes de compartilhar.',
    copyDroppedTitle: 'Algumas coisas não puderam vir na cópia',
    copyDroppedBody: 'Você só pode copiar o que já tem permissão para usar. Isto ficou de fora:',
    copyDroppedKnowledge: (name) => `${name} — conhecimento que não está compartilhado com você`,
    copyDroppedCapability: (name) => `${name} — uma habilidade que você não pode usar`,
    openCopy: 'Abrir sua cópia',
    ownerLabel: 'Pertence a',
    audienceLabel: 'Quem pode usar',
    /** Concorda com *agente* (masculino), que é o que esta data descreve em ambas as telas. */
    updatedLabel: 'Atualizado',
    /**
     * *Só quem gerencia X* em vez de *Só os gestores de X*: o inglês diz "Only X’s managers",
     * e *gestor/gestora* é flexionado. Como nada aqui sabe quem são essas pessoas, a forma com
     * **quem** é a única correta — e é a mesma construção da frase ratificada *Só quem mantém*.
     */
    audiencePrivate: (owner) => (owner.kind === 'user' ? `Só ${owner.name}` : `Só quem gerencia ${owner.group.name}`),
    audienceEveryone: 'Todas as pessoas que entram',
    audienceNobody: 'Ninguém ainda',
    audienceAnd: 'e',
    whatsNewTitle: 'Novidades',
    notFound: {
      title: 'Este agente não está disponível',
      body: 'Ele pode ter sido removido ou pode não estar compartilhado com você. Confira o link ou veja os agentes que você pode usar.'
    },
    loading: 'Carregando…',
    suspendedTitle: 'Este agente está pausado',
    suspendedBody: 'Um administrador pausou o agente. Você não pode iniciar uma nova conversa com ele agora.',
    archivedTitle: 'Este agente foi descontinuado',
    archivedBody: 'As conversas existentes continuam legíveis, mas não dá para iniciar novas.',
    draftTitle: 'Este agente ainda é um rascunho',
    draftBody: 'Só quem mantém o agente e seus coeditores podem vê-lo até que ele seja ativado.',
    replacementLink: 'Use o substituto',
    editAgent: 'Editar agente',
    viewDetails: 'Ver detalhes'
  },

  details: {
    title: 'Detalhes',
    readOnly: 'Você pode ver como este agente está configurado. Só quem mantém o agente pode mudá-lo.',
    backToAgent: 'Abrir a página do agente',
    identityTitle: 'Identidade',
    identityLead: 'Como este agente se apresenta.',
    nameLabel: 'Nome',
    taglineLabel: 'Resumo em uma linha',
    categoryLabel: 'Categoria',
    descriptionLabel: 'Para que serve',
    descriptionEmpty: 'Nenhuma descrição foi escrita para este agente.',
    instructionsTitle: 'Instruções',
    instructionsLead: 'As orientações permanentes que quem criou o agente escreveu para ele.',
    instructionsWithheld: 'As instruções ficam visíveis para quem mantém este agente.',
    instructionsWithheldBody:
      'O que o agente realmente vai fazer com elas está abaixo, escrito pelo sistema a partir das configurações reais, e não por quem o construiu.',
    startersTitle: 'Perguntas iniciais',
    startersLead: 'Sugestões oferecidas a quem abre uma conversa com ele.',
    startersEmpty: 'Nenhuma pergunta inicial é oferecida.',
    audienceTitle: 'Quem pode usar',
    audienceLead: 'Quem pode usar este agente e quem responde por ele.',
    stateLabel: 'Estado',
    /** Adjetivos concordando com *agente* (masculino). */
    state: {
      draft: 'Rascunho',
      published: 'Publicado',
      archived: 'Descontinuado',
      suspended: 'Pausado por um administrador'
    },
    notAvailable: {
      title: 'Este agente não está disponível',
      body: 'Ele pode ter sido removido ou pode não estar compartilhado com você. Veja os agentes que você pode usar.'
    },
    loadFailed: {
      title: 'Não foi possível carregar este agente',
      body: 'Isso costuma ser temporário. Tente de novo e avise um administrador se continuar acontecendo.'
    },
    tryAgain: 'Tentar novamente',
    allAgents: 'Todos os agentes',
    cardAction: (name) => `Detalhes de ${name}`
  },

  disclosure: {
    title: 'O que este agente sabe e pode fazer',
    subtitle: 'Escrito pelo sistema a partir das configurações reais do agente, não por quem o construiu.',
    knowledgeTitle: 'Conhecimento',
    knowledgeModeAttached: 'Responde apenas a partir destas bases de conhecimento.',
    knowledgeModeAll: 'Busca em tudo que você tem permissão para ler.',
    knowledgeEmpty: 'Nenhuma base de conhecimento está anexada.',
    knowledgeOwner: (name) => `Pertence a ${name}`,
    /**
     * As três frases falam da *base de conhecimento*, que é feminina — daí *usá-la*, *ela
     * responde*, *sem ela*. O inglês consegue dizer tudo com "it"; o português precisa
     * concordar, e a entidade é conhecida aqui, então a concordância é escrita (D-21).
     */
    coverage: {
      full: 'Você pode usá-la.',
      lent: (agent) => `Ela responde a você através de ${agent}. Você não pode abrir os documentos dela.`,
      none: (agent) => `Indisponível para você — ${agent} responde sem ela.`
    },
    coverageFull: 'Você recebe respostas de todo o conhecimento deste agente.',
    coveragePartial: 'Parte do conhecimento deste agente não está disponível para você.',
    capabilitiesTitle: 'O que ele pode fazer',
    capabilitiesEmpty: 'Ele só responde perguntas — não pode executar nenhuma ação em seu nome.',
    wordingReads: 'Lê informações',
    wordingActs: 'Pode executar ações — sempre pergunta antes',
    notAvailable: 'Indisponível para você',
    notAvailableHint:
      'O sistema por trás dela está limitado a grupos dos quais você não faz parte, então o agente não vai usá-la para você.',
    groundingTitle: 'Como ele responde',
    groundedOn: 'Responde apenas a partir do próprio conhecimento. Ele diz que não sabe em vez de chutar.',
    groundedOff:
      'Responde a partir do próprio conhecimento e de conhecimento geral, e mostra quais partes vieram de documentos.',
    memoryTitle: 'Preferências',
    memoryGlobal: 'Usa as preferências que você salvou.',
    memoryGlobalOff: 'Não usa as preferências que você salvou.',
    memoryLearns: 'Aprende novas preferências aqui.',
    memoryLearnsOff: 'Não aprende novas preferências aqui.'
  },

  /**
   * Pedir liberação a quem gerencia a base (PRD-26 D-17).
   *
   * *Pedir* em vez de *perguntar*: o inglês "Ask" aqui é um pedido de acesso, não uma
   * pergunta — e "perguntar a quem gerencia" mandaria a pessoa esperar uma resposta que não
   * é o que ela quer. Pedir não é conseguir, e cada frase continua dizendo isso.
   */
  ask: {
    action: 'Pedir a quem gerencia',
    actionNoMaintainer: 'Pedir a um administrador',
    title: (base) => `Pedir a quem gerencia ${base}`,
    titleNoMaintainer: 'Pedir a um administrador',
    consequence: (agent, base) =>
      `Se permitirem, ${agent} responde a partir de ${base} para todas as pessoas que usam o agente. Ninguém passa a abrir os documentos dela por causa disso.`,
    noMaintainer: (base) => `Ninguém mantém ${base} agora, então quem decide é um administrador.`,
    noteLabel: 'Uma nota para quem decide — opcional',
    notePlaceholder: 'O Jurídico precisa das regras da folha de pagamento para responder dúvidas de contrato.',
    send: 'Enviar',
    sending: 'Enviando…',
    cancel: 'Cancelar',
    /**
     * *você vai saber* em vez de *você será avisado*: o particípio concordaria com o gênero
     * de quem lê, que esta interface não conhece. A informação — que a resposta chega — é a
     * mesma (D-21).
     */
    sent: 'Pedido enviado. Você vai saber o que decidirem.',
    sentNoMaintainer: 'Pedido enviado. Um administrador vai decidir e você vai saber.',
    /**
     * Duas coisas que o inglês não precisa resolver e o português precisa (LOC-022).
     *
     * `when` chega de `formatAgo`, que em português já traz a preposição — "há 5 min." — então
     * a frase não acrescenta outra: nunca "Você pediu em há 5 min.".
     *
     * E o mesmo `when` às vezes termina em PONTO ABREVIATIVO: "há 5 min." e "21 de ago." têm,
     * "há 3 h" e "há 2 dias" não. O inglês ("5m ago") nunca tem, então lá a duração pode
     * fechar a frase. Aqui não pode: as três entradas abaixo foram reescritas para que o
     * tempo caia no MEIO da frase, senão metade dos estados renderiza "há 5 min..".
     */
    pending: (when) => `Você pediu ${when} e está aguardando quem gerencia.`,
    pendingNoMaintainer: (when) => `Você pediu ${when} e está aguardando um administrador.`,
    declined: (who, when) =>
      who ? `${who} respondeu ${when} e disse não.` : `Quem gerencia respondeu ${when} e disse não.`,
    declinedNote: (note) => `“${note}”`,
    askAgainLater: 'Você pode pedir de novo amanhã.',
    askAgain: 'Pedir de novo',
    already: 'Você já pediu e ninguém respondeu ainda.',
    outOfDate: 'Esta página estava desatualizada — o agente já responde a partir desta base para você.',
    failed: 'Não foi possível enviar. Tente de novo em instantes.',
    gone: 'Isto não está mais disponível para você, então não há o que pedir.',
    noLongerApplies:
      'Isto não se aplica mais — o agente pode ter parado de usar a base. Recarregue a página para ver como está agora.'
  },

  picker: {
    homeTitle: 'Quem responde',
    homeHint: 'Escolhido quando a conversa começa e fixo daí em diante.',
    homeTrigger: (agentName) => `${agentName} vai responder. Escolha outro agente.`,
    homeCurrent: 'escolhido',
    chatTitle: 'Falar com outro agente',
    chatHint: (agentName) => `Esta conversa fica com ${agentName}. Escolher outro inicia uma nova.`,
    chatHintAlone: (agentName) => `Esta conversa fica com ${agentName} e não pode ser movida.`,
    alone: 'É o único compartilhado com você até agora.',
    browse: 'Ver todos os agentes',
    chatTrigger: (agentName) => `${agentName} está respondendo. Inicie uma conversa com outro agente.`,
    chatCurrent: 'esta conversa'
  },

  notice: {
    title: (agentName) => `Antes de começar com ${agentName}`,
    intro: 'Uma coisa que vale saber antes.',
    actionsTitle: 'Este agente pode executar ações',
    actionsBody:
      'Ele pode fazer coisas fora desta conversa, como escrever em outro sistema. Ele sempre pergunta antes, e você pode dizer não.',
    memoryTitle: 'Este agente lembra preferências',
    memoryBody:
      'O que você contar sobre como gosta de trabalhar pode ser guardado e usado em conversas futuras com este agente. Você pode revisar e remover isso na página Memória.',
    acknowledge: 'Entendi — continuar',
    dismiss: 'Agora não',
    acknowledging: 'Salvando…',
    failed: 'Não foi possível salvar. Tente de novo.',
    beforeFirstChat: 'Há uma coisa para ler antes da sua primeira conversa com este agente.'
  },

  chat: {
    aboutLink: 'Sobre este agente',
    agentLabel: 'Respondendo',
    updatedNotice: 'Este agente foi atualizado depois que você abriu esta conversa.',
    updatedNoticeLink: 'Ver o que mudou',
    dismissUpdate: 'Dispensar',
    openingGreeting: (firstName) => `Olá, ${firstName}.`,
    openingFallback: 'Pergunte o que quiser. Cada resposta mostra os trechos que usou e quanto custou.',
    suspendedComposer: 'Este agente está pausado, então não dá para enviar novas mensagens.',
    archivedComposer: 'Este agente foi descontinuado, então não dá para enviar novas mensagens.',
    draftComposer:
      'Este agente não está ativado, então não dá para enviar novas mensagens. Ative-o para continuar usando.',
    unavailableComposer: (agent) =>
      `Você não pode mais usar ${agent ?? 'este agente'}, então não dá para enviar novas mensagens. Esta conversa continua legível.`,
    composerPlaceholder: 'Faça uma pergunta…',
    /** O inglês diz "Assistant" e quer dizer o produto, que é o único lugar onde essa palavra cabe. */
    unknownAgent: 'Assistente'
  },

  start: {
    browseAll: 'Ver todos os agentes',
    failed: 'Não foi possível iniciar a conversa. Tente de novo em instantes.',
    empty: {
      title: 'Nenhum agente foi compartilhado com você ainda',
      body: 'Um administrador pode compartilhar um com você, ou você pode criar o seu.'
    }
  },

  notifications: {
    open: 'Notificações',
    /** *notificação* é feminina, então o particípio do plural é *lidas* — e zero é singular. */
    openWithUnread: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `${fmt.formatCount(count)} notificação não lida`
        : `${fmt.formatCount(count)} notificações não lidas`,
    title: 'Notificações',
    close: 'Fechar notificações',
    markAllRead: 'Marcar todas como lidas',
    unreadDot: 'Não lida',
    empty: {
      title: 'Nada para colocar em dia',
      body: 'Solicitações esperando por você, respostas ao que você pediu e novidades sobre o que você mantém aparecem aqui.'
    },
    failed: 'Não foi possível carregar as notificações.'
  }
}
